/**
 * Bygg lagermotoren for ÉN flate. Kall den én gang i appens `lib/lager.ts` og
 * eksporter funksjonene derfra — da ser resten av appen et lager med sine egne
 * typer, og pakken slipper å kjenne dem.
 */
export function lagLagermotor(valg) {
    const naa = valg.naa ?? (() => Date.now());
    const borteMaks = valg.borteMaks ?? 50;
    function artRang(art) {
        const i = valg.rekkefolge.indexOf(art);
        return i >= 0 ? i : valg.rekkefolge.length;
    }
    function sorterSensorer(a, b) {
        return artRang(a.art) - artRang(b.art)
            || a.navn.localeCompare(b.navn, 'no')
            || a.id.localeCompare(b.id, 'no');
    }
    /** Sørg for at sensoren finnes i lista. Se `ukjentSensor` i `LagerValg`. */
    function kjennSensor(sensorer, sensorId) {
        if (sensorer.some((s) => s.id === sensorId))
            return sensorer;
        return [...sensorer, valg.ukjentSensor(sensorId)].sort(sorterSensorer);
    }
    // ── De tre uforanderlige oppdateringene ─────────────────────────────────
    //
    // Hver av dem har ÉN cast, og den er uunngåelig: TypeScript kan ikke bevise at
    // `{...k, felt}` fortsatt er `K` når `K` er en typeparameter — kalleren kan ha
    // en snevrere type enn skranken. Alternativet var å la hver app sende inn en
    // «oppdater dette feltet»-funksjon, altså å flytte samme problem ut i alle
    // apper. Feltene og verdiene er typet mot `K`/`S`, så kompilatoren fanger
    // fortsatt feilstavede felt og feil verdityper.
    /** Legg en observasjon på en kontakt, deduplisert på id, nyeste tid vinner. */
    function medObservasjon(k, o) {
        const uten = k.observasjoner.filter((x) => x.id !== o.id);
        return { ...k, observasjoner: [...uten, o], sisteTsMs: Math.max(k.sisteTsMs, o.tsMs) };
    }
    function medFlagg(k, flagg) {
        return { ...k, flagg };
    }
    function medMeldtFeil(s, merknad) {
        return { ...s, tilstand: 'feil', merknad };
    }
    function tomtLager() {
        return { post: null, sensorer: [], kontakter: [], borte: [], sisteMaalingPerSensor: {} };
    }
    function bruk(t, m) {
        switch (m.type) {
            case 'post':
                return { ...t, post: m.post };
            case 'sensor': {
                const uten = t.sensorer.filter((s) => s.id !== m.sensor.id);
                return { ...t, sensorer: [...uten, m.sensor].sort(sorterSensorer) };
            }
            case 'sensor-borte': {
                // Sensoren FJERNES IKKE. Den blir stående med tilstand `feil` og broens
                // årsak, for en sensor som forsvinner ut av stolpen er en sensor ingen
                // savner. Like mange rader ut som inn — alltid.
                const sensorer = t.sensorer.map((s) => (s.id === m.sensorId
                    ? medMeldtFeil(s, m.arsak)
                    : s));
                return { ...t, sensorer };
            }
            case 'observasjon': {
                const o = m.observasjon;
                const sensorer = kjennSensor(t.sensorer, o.sensorId);
                const sisteMaalingPerSensor = {
                    ...t.sisteMaalingPerSensor,
                    [o.sensorId]: Math.max(t.sisteMaalingPerSensor[o.sensorId] ?? 0, o.tsMs),
                };
                if (!o.kontaktId) {
                    // Uassosiert observasjon: sensoren MÅLTE noe (stolpen skal vise det),
                    // men fusjonen har ikke knyttet den til en kontakt ennå.
                    return { ...t, sensorer, sisteMaalingPerSensor };
                }
                const kontakter = t.kontakter.map((k) => (k.id === o.kontaktId ? medObservasjon(k, o) : k));
                return { ...t, sensorer, kontakter, sisteMaalingPerSensor };
            }
            case 'kontakt': {
                const inn = m.kontakt;
                const fins = t.kontakter.find((k) => k.id === inn.id);
                // Broen er kilden for alt annet enn OPERATØRENS eget flagg. Det flagget er
                // et menneskes vurdering, og en oppdatering fra broen skal ikke kunne
                // nulle den ut — det ville gjort «merket trussel» til noe som forsvant av
                // seg selv, og da slutter operatøren å bruke flagget.
                const slått = fins
                    ? medFlagg(inn, fins.flagg === valg.noytraltFlagg ? inn.flagg : fins.flagg)
                    : inn;
                const uten = t.kontakter.filter((k) => k.id !== inn.id);
                const sisteMaalingPerSensor = { ...t.sisteMaalingPerSensor };
                for (const o of inn.observasjoner) {
                    sisteMaalingPerSensor[o.sensorId] = Math.max(sisteMaalingPerSensor[o.sensorId] ?? 0, o.tsMs);
                }
                let sensorer = t.sensorer;
                for (const o of inn.observasjoner)
                    sensorer = kjennSensor(sensorer, o.sensorId);
                return {
                    ...t,
                    sensorer,
                    kontakter: [...uten, slått],
                    // En kontakt som kommer tilbake skal ut av borte-loggen, ellers står den
                    // både som aktiv og som avmeldt.
                    borte: t.borte.filter((b) => b.kontaktId !== inn.id),
                    sisteMaalingPerSensor,
                };
            }
            case 'kontakt-borte': {
                const fantes = t.kontakter.some((k) => k.id === m.kontaktId);
                return {
                    ...t,
                    kontakter: t.kontakter.filter((k) => k.id !== m.kontaktId),
                    // Loggfør bare kontakter vi faktisk hadde. En «borte»-melding for en
                    // ukjent kontakt er et rekkefølgeproblem hos broen, ikke informasjon
                    // til operatøren.
                    borte: fantes
                        ? [{ kontaktId: m.kontaktId, arsak: m.arsak, tsMs: naa() }, ...t.borte].slice(0, borteMaks)
                        : t.borte,
                };
            }
        }
    }
    return {
        tomtLager,
        bruk,
        settFlagg(t, kontaktId, flagg) {
            return { ...t, kontakter: t.kontakter.map((k) => (k.id === kontaktId ? medFlagg(k, flagg) : k)) };
        },
        artOppslag(t) {
            const kart = new Map(t.sensorer.map((s) => [s.id, s.art]));
            return (id) => kart.get(id);
        },
        sorterSensorer,
        artRang,
    };
}
//# sourceMappingURL=lager.js.map